// Story 029 — Link icon + popover for a defect.
//
// Renders a small `<IconButton aria-label="Linked defects (N)" />` on
// every defect row that has ≥1 link. Click → popover lists each
// sibling (image filename + thumbnail + catalogue label + unlink
// button). Clicking a sibling row activates that image AND scrolls
// to the sibling defect; the Unlink button fires the unlink mutation.
import { useState } from "react";
import {
  Avatar,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useImageActivationStore } from "../analysis/state/imageActivationStore.js";
import { useSelectionStore } from "../analysis/state/selectionStore.js";
import { useDefectLinksQuery, useUnlinkDefectsMutation } from "./queries.js";
import {
  denyReasonTooltip,
  type DefectEditPermission,
} from "../analysis/editPermission.js";
import { formatSeverity } from "./severityDisplay.js";
import { LinkIcon, UnlinkIcon } from "@aeriosiq/ui/icons";

interface Props {
  defectId: string;
  inspectionId: string;
  editPermission?: DefectEditPermission;
}

export function DefectLinkIcon({
  defectId,
  inspectionId,
  editPermission,
}: Props): React.ReactElement | null {
  const linksQuery = useDefectLinksQuery(defectId);
  const [open, setOpen] = useState(false);
  const setActiveImage = useImageActivationStore((s) => s.setActive);
  const setActiveSelection = useSelectionStore((s) => s.selectOne);
  const activeImageId = useImageActivationStore((s) => s.activeImageId);
  const unlink = useUnlinkDefectsMutation(inspectionId);

  const items = linksQuery.data?.items ?? [];
  const count = items.length;
  // No links → render nothing. The icon only appears when there's a
  // relationship to expose (story 029 §"In scope").
  if (count === 0) return null;

  const canEdit = editPermission?.allowed !== false;
  const denyTooltip =
    editPermission && !editPermission.allowed
      ? denyReasonTooltip(editPermission.reason)
      : "";

  return (
    <>
      <Tooltip title={`Linked defects (${String(count)})`}>
        <IconButton
          size="small"
          aria-label={`Linked defects (${String(count)})`}
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
          sx={{ ml: 0.5 }}
        >
          <LinkIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        aria-labelledby={`linked-defects-${defectId}`}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id={`linked-defects-${defectId}`}>
          Linked defects ({count})
        </DialogTitle>
        <DialogContent dividers>
          <List dense disablePadding>
            {items.map((it) => (
              <ListItem
                key={it.linkId}
                data-link-id={it.linkId}
                secondaryAction={
                  <Tooltip title={canEdit ? "Unlink" : denyTooltip}>
                    <span>
                      <IconButton
                        edge="end"
                        size="small"
                        aria-label="Unlink"
                        aria-disabled={!canEdit}
                        disabled={!canEdit || unlink.isPending}
                        onClick={() => {
                          if (!canEdit) return;
                          unlink.mutate(
                            {
                              linkId: it.linkId,
                              masterDefectId: it.isMaster
                                ? defectId
                                : it.siblingDefectId,
                              linkedDefectId: it.isMaster
                                ? it.siblingDefectId
                                : defectId,
                              dataAcquisitionId: inspectionId,
                              imageId: activeImageId ?? "",
                            },
                            {
                              onSuccess: () => {
                                // Close if no remaining items (we'll
                                // re-render after invalidate; the empty
                                // popover would already be unmounted by
                                // the icon's return-null path).
                              },
                            },
                          );
                        }}
                      >
                        <UnlinkIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                }
              >
                <ListItemAvatar>
                  <Avatar
                    variant="rounded"
                    src={it.siblingThumb120}
                    alt=""
                    sx={{ width: 56, height: 40 }}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box
                      component="button"
                      type="button"
                      onClick={() => {
                        setActiveImage(it.siblingImageId);
                        setActiveSelection(it.siblingDefectId);
                        setOpen(false);
                      }}
                      sx={{
                        background: "none",
                        border: "none",
                        p: 0,
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {it.siblingCatalogueLabel}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Stack
                      direction="row"
                      spacing={1}
                      component="span"
                      sx={{ color: "text.secondary" }}
                    >
                      <Typography variant="caption" component="span">
                        {it.isMaster ? "→ linked" : "← master"}
                      </Typography>
                      <Typography variant="caption" component="span">
                        {/* 8.0.0 — null = "Not graded"; the link list only
                            carries the derived enum, so the enum text stays
                            for graded siblings. */}
                        {formatSeverity(it.siblingSeverity)}
                      </Typography>
                    </Stack>
                  }
                  slotProps={{ secondary: { component: "div" } }}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </>
  );
}
